import React, { useEffect, useRef, useState } from 'react'
import BigModal from './BigModal'
import {
  Typography,
  Button,
  LinearProgress,
  Stack,
  useMediaQuery,
} from '@mui/material'
import API from '../../services/API'

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']

const getExt = (v) => {
  if (!v) return ''
  const s = String(v).split('?')[0].split('#')[0]
  return s.includes('.') ? s.split('.').pop().toLowerCase() : ''
}

const inferTypeFromExt = (ext) => {
  if (ext === 'pdf') return 'pdf'
  if (IMAGE_EXTS.includes(ext)) return 'image'
  return 'other'
}

const PDFViewer = ({
  open,
  onClose,
  file: initialFile,
  title,
  s3key,
  filename, // only for download name / content-disposition
  children,
}) => {
  const [loading, setLoading] = useState(!initialFile)
  const [file, setFile] = useState(initialFile ?? null)
  const [fileType, setFileType] = useState(null)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const autoOpenedRef = useRef(false)

  const downloadFile = async (inlineView = true) => {
    try {
      const actualExt = (
        s3key?.split('?')[0].split('#')[0].split('.').pop() || ''
      ).toLowerCase()
      const name = filename || s3key?.split('/').pop() || 'download'

      // Ensure the download filename has the real extension (optional but recommended)
      const downloadName = actualExt
        ? name.replace(/\.[^.]+$/, '') + '.' + actualExt
        : name

      const payload = {
        key: s3key,
        inlineView,
        // ✅ CRITICAL: only send filename for downloads, not inline preview
        ...(inlineView ? {} : { filename: downloadName }),
      }

      const res = await API.post('/gets3downloadurl', payload)

      setFile(res.data)

      if (!inlineView) {
        const a = document.createElement('a')
        a.href = res.data
        a.download = downloadName
        document.body.appendChild(a)
        a.click()
        a.remove()
        onClose?.()
      }
    } catch (e) {
      console.log(e)
    } finally {
      if (inlineView) setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return

    // Reset on each open (prevents leaking pdf/image state between files)
    autoOpenedRef.current = false
    setFile(initialFile ?? null)
    setLoading(!initialFile)
    setFileType(null)

    // If a real File/Blob was passed, infer from MIME type
    if (initialFile && typeof initialFile === 'object' && initialFile?.type) {
      if (initialFile.type.includes('pdf')) setFileType('pdf')
      else if (initialFile.type.startsWith('image/')) setFileType('image')
      else setFileType('other')
      setLoading(false)
      return
    }

    // If a URL string was passed, infer from URL extension
    if (typeof initialFile === 'string') {
      const t = inferTypeFromExt(getExt(initialFile))
      setFileType(t)
      setLoading(false)
      return
    }

    // Otherwise: infer from S3 key ONLY (ignore filename completely)
    const ext = getExt(s3key)
    const t = inferTypeFromExt(ext)
    setFileType(t)

    if (t === 'other') {
      setLoading(true)
      downloadFile(false)
    } else {
      setLoading(true)
      downloadFile(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFile, s3key])

  // Sanity check: if the signed URL comes back with a recognizable extension, sync type to it
  useEffect(() => {
    if (!open) return
    if (typeof file !== 'string') return
    const tFromUrl = inferTypeFromExt(getExt(file))
    if (tFromUrl && tFromUrl !== fileType) setFileType(tFromUrl)
  }, [open, file, fileType])

  // Auto-open PDF on mobile when ready
  useEffect(() => {
    if (!open) return
    if (!isMobile) return
    if (fileType !== 'pdf') return
    if (!file || loading) return
    if (autoOpenedRef.current) return

    autoOpenedRef.current = true
    window.open(file, '_blank', 'noopener,noreferrer')
  }, [open, isMobile, fileType, file, loading])

  const handleOpenInNewTab = () => {
    if (!file) return
    window.open(file, '_blank', 'noopener,noreferrer')
  }

  return (
    <BigModal
      open={open && !!fileType}
      onClose={onClose}
      style={{ height: isMobile ? '85vh' : 600 }}
    >
      <Stack flexGrow={1} flex={1} height="100%" gap={1}>
        <Typography variant="title2">{title}</Typography>

        {loading ? (
          <LinearProgress />
        ) : (
          <Stack gap={1} flexGrow={1} minHeight={0}>
            {children}

            {fileType === 'pdf' ? (
              isMobile ? (
                <Stack gap={1}>
                  <Typography variant="body2">
                    PDF preview isn’t reliable inside the in-app viewer on
                    mobile.
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      onClick={handleOpenInNewTab}
                      fullWidth
                    >
                      Open PDF
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => downloadFile(false)}
                      fullWidth
                    >
                      Download
                    </Button>
                    <Button
                      variant="outlined"
                      color="anRed"
                      onClick={() => onClose?.()}
                      fullWidth
                    >
                      Close
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <iframe
                  src={file}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="PDF Preview"
                />
              )
            ) : fileType === 'image' ? (
              <img
                src={file}
                alt={title || filename || s3key?.split('/').pop()}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 4,
                }}
              />
            ) : (
              <Typography>
                This file cannot be previewed.{' '}
                <Button href={file} target="_blank" rel="noreferrer">
                  Download
                </Button>
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </BigModal>
  )
}

export default PDFViewer
